// Mirrors iOS ChartDetailView: an expandable "Chart Details" card describing
// the selected chart with key insights and usage hints.
import { useState } from 'react';
import { Info, ChevronDown, ChevronUp, CheckCircle2, Clock, TrendingUp, Star, LayoutGrid, CalendarClock } from 'lucide-react';

const DETAILS = {
  hourly: {
    label: 'Hourly', Icon: Clock, color: 'text-blue-500',
    description: 'Shows revenue breakdown by hour of the day. This helps you understand when your restaurant is busiest and when sales peak throughout the day.',
    insights: [
      'Identify peak business hours for optimal staffing',
      'Plan prep work during slower periods',
      'Adjust operating hours based on demand',
    ],
    howToUse: 'Use this chart to schedule staff during peak hours and plan food prep during slower periods. Consider extending hours if you see consistent late-night demand.',
  },
  daily: {
    label: 'Daily', Icon: TrendingUp, color: 'text-green-500',
    description: 'Displays daily revenue trends over the selected time period. Track your growth patterns, identify high-performing days, and spot trends in your sales.',
    insights: [
      'Track day-over-day growth trends',
      'Identify seasonal patterns',
      'Compare performance across different time periods',
    ],
    howToUse: 'Monitor this chart regularly to spot trends. If you see a dip, investigate potential causes. If you see growth, identify what\'s working and do more of it.',
  },
  topItems: {
    label: 'Top Items', Icon: Star, color: 'text-[#FF6B35]',
    description: 'Lists your best-selling menu items ranked by quantity sold. This data helps you understand customer preferences and optimize your menu offerings.',
    insights: [
      'Focus on promoting best-sellers',
      'Ensure popular items are always in stock',
      'Consider removing slow-moving items',
    ],
    howToUse: 'Promote your top items in marketing materials. Ensure you always have ingredients for these items. Consider creating combo deals featuring popular items.',
  },
  categories: {
    label: 'Categories', Icon: LayoutGrid, color: 'text-purple-500',
    description: 'Compares sales performance across different menu categories. See which types of items (appetizers, mains, desserts, etc.) drive the most revenue.',
    insights: [
      'Balance your menu across categories',
      'Identify underperforming categories',
      'Plan promotions for specific categories',
    ],
    howToUse: 'Use this to balance your menu. If one category dominates, consider expanding it. If a category underperforms, refresh those items or adjust pricing.',
  },
  weeklyTrends: {
    label: 'Weekly Trends', Icon: CalendarClock, color: 'text-indigo-500',
    description: 'Shows revenue and order patterns by day of the week. Identify which days are busiest and plan staffing and inventory accordingly.',
    insights: [
      'Plan weekly staffing schedules',
      'Adjust inventory for busy days',
      'Create day-specific promotions',
    ],
    howToUse: 'Plan your week based on these patterns. Order more inventory before busy days. Schedule more staff on high-volume days. Create special promotions for slower days.',
  },
};

export default function ChartDetailSection({ chartType }) {
  const [expanded, setExpanded] = useState(false);
  const d = DETAILS[chartType];
  if (!d) return null;
  const { Icon } = d;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <Info size={18} className="text-blue-500" />
        <span className="flex-1 text-sm font-semibold text-gray-900">Chart Details</span>
        {expanded
          ? <ChevronUp size={18} className="text-blue-500" />
          : <ChevronDown size={18} className="text-blue-500" />}
      </button>

      {expanded && (
        <div className="mx-4 mb-4 rounded-xl bg-gray-50/70 px-4 py-3 space-y-4">
          <div className="flex items-center gap-2">
            <Icon size={20} className={d.color} />
            <span className="text-base font-bold text-gray-900">{d.label}</span>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">What does this chart show?</p>
            <p className="text-sm text-gray-800">{d.description}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Key Insights</p>
            <ul className="space-y-1.5">
              {d.insights.map((i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-800">
                  <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />
                  {i}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">How to use this data</p>
            <p className="text-sm text-gray-800">{d.howToUse}</p>
          </div>
        </div>
      )}
    </div>
  );
}
